import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CustomerAPI } from "../../QueueBoard/api/customerApi";
import { LookupAPI } from "../../QueueBoard/api/lookupDataApi";
import { WorkOrderQueueAPI } from "../../QueueBoard/api/workOrderApi";
import { getAuditUserName } from "../../common/Utils/auditUser";
import {
  DropdownOption,
  CustomerOption,
  ServiceCodeOption,
  CreateWorkOrderRequest,
  CreateWorkOrderResponse,
  SmartWorkOrderApiRequest,
} from "./types";

/**
 * Query keys for react-query cache management
 */
export const woCreationKeys = {
  all: ["smartWoCreation"] as const,
  customers: (searchTerm: string) =>
    [...woCreationKeys.all, "customers", searchTerm] as const,
  locations: () => [...woCreationKeys.all, "locations"] as const,
  woStatuses: () => [...woCreationKeys.all, "woStatuses"] as const,
  woPriorities: () => [...woCreationKeys.all, "woPriorities"] as const,
  salesPersons: () => [...woCreationKeys.all, "salesPersons"] as const,
  technicians: () => [...woCreationKeys.all, "technicians"] as const,
  serviceCodes: () => [...woCreationKeys.all, "serviceCodes"] as const,
};

// ===========================
// Data Fetching Functions
// ===========================

/**
 * Fetch customers by search term
 */
export const searchCustomers = async (
  searchTerm: string,
): Promise<CustomerOption[]> => {
  if (!searchTerm || searchTerm.length < 2) return [];

  const customers = await CustomerAPI.searchCustomersByName(searchTerm);
  if (!customers) return [];

  return customers.map((customer) => ({
    identity: customer.identity,
    name: customer.name || "",
    number: customer.number || "",
  }));
};

/**
 * Fetch locations from LookupAPI
 */
export const fetchLocations = async (): Promise<DropdownOption[]> => {
  const locations = await LookupAPI.getLocations();
  return locations.map((location) => ({
    identity: location.identity,
    displayText: location.displayText,
  }));
};

/**
 * Fetch work order statuses from LookupAPI
 */
export const fetchWoStatuses = async (): Promise<DropdownOption[]> => {
  const statuses = await LookupAPI.getWorkOrderStatuses();
  return statuses.map((status) => ({
    identity: status.identity,
    displayText: status.displayText,
  }));
};

/**
 * Fetch work order priorities from LookupAPI
 */
export const fetchWoPriorities = async (): Promise<DropdownOption[]> => {
  const priorities = await LookupAPI.getWorkOrderPriorityList();
  return priorities.map((priority) => ({
    identity: priority.identity,
    displayText: priority.displayText,
  }));
};

/**
 * Fetch sales persons from LookupAPI
 */
export const fetchSalesPersons = async (): Promise<DropdownOption[]> => {
  const persons = await LookupAPI.getSalesPersons();
  return persons.map((person) => ({
    identity: person.identity,
    displayText: person.userName,
  }));
};

/**
 * Fetch technicians from LookupAPI
 */
export const fetchTechnicians = async (): Promise<DropdownOption[]> => {
  const techs = await LookupAPI.getTechnicians();
  return techs.map((tech) => ({
    identity: tech.identity,
    displayText: tech.userName,
  }));
};

/**
 * Fetch service codes from LookupAPI
 */
export const fetchServiceCodes = async (): Promise<ServiceCodeOption[]> => {
  const codes = await LookupAPI.getServiceCodes();
  return codes.map((code) => ({
    identity: code.identity,
    code: code.serviceCode,
    description: code.description,
    displayText: `${code.serviceCode} - ${code.description}`,
  }));
};

/**
 * Create smart work order for technician calendar
 * This creates a work order and automatically schedules it for the selected technician
 */
export const createSmartWOForTechCalendar = async (
  data: CreateWorkOrderRequest,
): Promise<CreateWorkOrderResponse> => {
  // Transform the request to match the backend DTO
  const auditUser = getAuditUserName();
  const createdBy = data.createdBy?.trim() || auditUser;
  const modifiedBy = data.modifiedBy?.trim() || auditUser;

  const workOrderData = {
    customerIdentity: data.customerIdentity,
    locationIdentity: data.locationIdentity,
    woStatusIdentity: data.woStatusIdentity,
    woPriorityIdentity: data.woPriorityIdentity,
    salesPersonIdentity: data.salesPersonIdentity,
    technicianIdentity: data.technicianIdentity,
    status: data.status,
    actionType: data.actionType,
    createdBy,
    createdDate: data.createdDate,
    modifiedBy,
    modifiedDate: data.modifiedDate,
    // Segment data - matches SmartWorkOrderSegmentRequest
    segment: {
      displayText: data.segments[0]?.displayText || "",
      serviceCodeIdentity: data.segments[0]?.serviceCodeIdentity || 0,
    },
    // Schedule data for technician calendar - matches SmartWorkOrderScheduleRequest
    // Use workDate from form data, fall back to preSelectedDate for backward compatibility
    schedule: data.technicianIdentity
      ? {
          technicianId: data.technicianIdentity,
          workDate: data.workDate || data.preSelectedDate || new Date().toISOString().split("T")[0],
          estimatedHours: 0, // Will be populated from service code
          overCapacity: false,
          technicianOff: false,
        }
      : null,
  };

  // Use the new dedicated endpoint for creating smart work orders
  const result: any = await WorkOrderQueueAPI.createSmartWOForTechCalendar(workOrderData);

  // Check for null/undefined result
  if (!result) {
    throw new Error("Failed to create work order - no response from server");
  }

  // Check for error indicators in the response
  // Backend may return { isSuccess: false, message: "..." } on error
  if (result.isSuccess === false) {
    throw new Error(result.message || result.errorMessage || "Failed to create work order");
  }

  // Check if the response has the expected success fields
  if (!result.invoiceDocumentIdentity || result.invoiceDocumentIdentity === 0) {
    throw new Error(result.message || "Failed to create work order - invalid response from server");
  }

  return {
    invoiceDocumentIdentity: result.invoiceDocumentIdentity,
    documentNumber: result.documentNumber || "",
    message: result.message || "Work order created and scheduled successfully",
  };
};

// ===========================
// React Query Hooks
// ===========================

/**
 * Hook to create smart work order for technician calendar with mutation
 */
export const useCreateSmartWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSmartWOForTechCalendar,
    onSuccess: () => {
      // Invalidate work orders list to refresh data
      queryClient.invalidateQueries({ queryKey: ["workOrders"] });
      queryClient.invalidateQueries({ queryKey: ["technicianCalendar"] });
    },
  });
};
